import { useState, type ReactNode } from 'react'
import { ChromeIcon } from '@/components/ChromeIcon'
import { BrandLockup } from '@/components/BrandLockup'
import { MARKETING_SITE_URL } from '@/components/help/articleIndex'
import s from './auth-shell.module.css'

/* ── Required-marker + inline-error pattern ───────────────────────────────
 *
 * #445 landed this on the outreach signup form first. Sign-up, sign-in, and
 * password reset now carry one contract from the auth kit instead of three
 * near-copies that can drift.
 *
 * The contract is three things together, and all three are what make it
 * accessible rather than decorative:
 *   1. `*` on the label of every required field, `aria-hidden` — screen readers
 *      get requiredness from `aria-required`/validation, not from punctuation.
 *   2. `aria-invalid` + `aria-describedby` on the control, pointing at
 *   3. a `role="alert"` message rendered beneath it.
 *
 * The asterisk is the only convention. No "(optional)" suffix appears on the
 * same form; an unmarked field is optional by implication.
 *
 * `authFieldProps` exists so 2 and 3 cannot come apart: a control wired by hand
 * can carry `aria-invalid` while pointing `aria-describedby` at an id nothing
 * renders, which reads to a screen reader as an error with no text.
 */

/** The `*` that marks a required field. Decorative — see the contract above. */
export function RequiredMark() {
  return (
    <span className={s.req} aria-hidden="true">
      *
    </span>
  )
}

/** The inline message under an invalid control. Renders nothing when valid. */
export function FieldError({ id, message }: { id: string; message?: string | null }) {
  if (!message) return null
  return (
    <span id={id} className={s.fieldError} role="alert">
      {message}
    </span>
  )
}

/**
 * The control-side half, so `aria-invalid`, `aria-describedby` and the invalid
 * border are decided once from one message. Spread onto the input; pass the
 * same `id` to the FieldError rendered beneath it.
 */
export function authFieldProps(id: string, message?: string | null, baseClass?: string) {
  const invalid = Boolean(message)
  return {
    'aria-invalid': invalid || undefined,
    'aria-describedby': invalid ? id : undefined,
    className: [baseClass, invalid ? s.fieldInputInvalid : ''].filter(Boolean).join(' '),
  }
}

/**
 * The submit button stays enabled on an invalid form — a disabled button
 * explains nothing — and the first invalid control is focused and scrolled
 * into view instead.
 */
export function focusFirstInvalid(controlId: string) {
  window.requestAnimationFrame(() => {
    const el = document.getElementById(controlId)
    el?.focus()
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  })
}

/**
 * The frame every unauthenticated page shares — /login, /admin/login and
 * /reset-password all render inside it, so the contractor and admin sign-ins are
 * the same screen with one difference: the ADMIN badge next to the lockup.
 *
 * `admin` only toggles that badge. Don't branch anything else on it.
 *
 * The brand bar is also the page's way out: the lockup links back to the
 * marketing landing page and Help (top right) opens the help center in a new
 * tab — both resolved through MARKETING_SITE_URL so a local
 * VITE_MARKETING_SITE_URL override steers them like the Help drawer.
 */
export function AuthShell({ admin = false, spacious = false, children }: { admin?: boolean; spacious?: boolean; children: ReactNode }) {
  const [fieldNavigation, setFieldNavigation] = useState<'pointer' | 'keyboard'>('pointer')
  return (
    <div
      className={s.root}
      data-field-navigation={fieldNavigation}
      onPointerDownCapture={() => setFieldNavigation('pointer')}
      onKeyDownCapture={(event) => { if (event.key === 'Tab') setFieldNavigation('keyboard') }}
    >
      <div className={s.brand}>
        <a href={MARKETING_SITE_URL} className={s.brandWordmark} aria-label="Ten Four">
          <BrandLockup label={null} size="1.625rem" />
        </a>
        {admin && <span className={s.brandBadge}>Admin</span>}
        <a href={`${MARKETING_SITE_URL}/help`} target="_blank" rel="noreferrer" className={s.brandHelp}>
          <ChromeIcon name="help" size={15} />
          <span>Help</span>
        </a>
      </div>

      <main className={[s.content, spacious ? s.spacious : ''].filter(Boolean).join(' ')}>{children}</main>

      <p className={s.legal}>
        <a href="/terms" className={s.legalLink}>Terms of Service</a>
        {' and '}
        <a href="/privacy" className={s.legalLink}>Privacy Policy</a>
      </p>
    </div>
  )
}
